import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Plus, PencilSimple, Trash, ArrowUp, ArrowDown, Image as ImageIcon, Info, UploadSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Turn a stored `image_path` (either an absolute URL, a
// leading-slash static asset like `/promo-banner-ato.jpg`, or a
// backend storage key) into something the browser can render.
const bannerSrc = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return path;  // /public/ static
  return `${API}/files/${path}`;
};

export const BannersManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', data: null });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/banners`, { withCredentials: true });
      setBanners(data || []);
    } catch { toast.error('Failed to load banners'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => setDialog({
    open: true, mode: 'create',
    data: { image_path: '', link_url: '', is_active: true },
  });

  const openEdit = (b) => setDialog({
    open: true, mode: 'edit',
    data: {
      ...b,
      link_url: b.link_url || '',
      is_active: b.is_active !== false,
    },
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload?purpose=banner`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDialog(d => ({ ...d, data: { ...d.data, image_path: data.path } }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const save = async () => {
    const { mode, data } = dialog;
    if (!data.image_path) { toast.error('Please upload an image'); return; }
    if (data.link_url && !(data.link_url.startsWith('/') || data.link_url.startsWith('http'))) {
      toast.error("Link URL must start with '/' or 'http(s)://'"); return;
    }
    const payload = {
      image_path: data.image_path,
      link_url: data.link_url ? data.link_url.trim() : null,
      is_active: !!data.is_active,
    };
    try {
      if (mode === 'create') {
        await axios.post(`${API}/admin/banners`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/banners/${data.banner_id}`, payload, { withCredentials: true });
      }
      setDialog({ open: false, mode: 'create', data: null });
      fetchAll();
      toast.success(mode === 'create' ? 'Banner added' : 'Banner updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const toggleActive = async (b) => {
    const nextActive = !(b.is_active !== false);
    try {
      await axios.put(`${API}/admin/banners/${b.banner_id}`, { is_active: nextActive }, { withCredentials: true });
      fetchAll();
      toast.success(nextActive ? 'Banner enabled' : 'Banner disabled');
    } catch { toast.error('Failed'); }
  };

  const remove = async (b) => {
    try {
      await axios.delete(`${API}/admin/banners/${b.banner_id}`, { withCredentials: true });
      fetchAll();
      toast.success('Banner deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= banners.length) return;
    const reordered = [...banners];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      await axios.put(`${API}/admin/banners/reorder`, { banner_ids: reordered.map(b => b.banner_id) }, { withCredentials: true });
      fetchAll();
    } catch { toast.error('Reorder failed'); }
  };

  if (loading) return <div className="text-center py-12"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="banners-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-text_main">Home Banners Carousel</h3>
          <p className="text-sm text-text_muted mt-1">Image-only banners displayed at the top of the landing page. Auto-rotate every 5s when more than one is active.</p>
        </div>
        <Button onClick={openCreate} data-testid="banner-add-btn">
          <Plus size={16} weight="bold" className="mr-1" /> Add Banner
        </Button>
      </div>

      <div className="bg-info-soft border border-info/40 rounded-lg px-3 py-2 text-xs text-text_main flex items-start gap-2">
        <Info size={14} className="text-info shrink-0 mt-0.5" weight="fill" />
        <span>Required image dimensions: <strong>1821 × 864 px</strong> (ratio ~2.11:1, JPG/PNG/WEBP, max 10 MB). All banners MUST match this exact ratio — the carousel uses <code>object-contain</code> on a cream background so any off-ratio image will letterbox instead of crop. The default row mirrors the platform banner configured in Appearance.</span>
      </div>

      <div className="grid gap-3">
        {banners.length === 0 && (
          <p className="text-sm text-text_muted text-center py-8 bg-white border border-border rounded-lg">No banners yet.</p>
        )}
        {banners.map((b, idx) => {
          const active = b.is_active !== false;
          return (
            <div key={b.banner_id} className={`flex items-center gap-3 p-3 bg-white border border-border rounded-lg ${active ? '' : 'opacity-60'}`} data-testid={`admin-banner-${b.banner_id}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="disabled:opacity-30" aria-label="Move up"><ArrowUp size={12} weight="bold" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === banners.length - 1} className="disabled:opacity-30" aria-label="Move down"><ArrowDown size={12} weight="bold" /></button>
              </div>
              <div className="w-32 h-10 rounded border border-border overflow-hidden bg-surface_muted flex-shrink-0">
                <img src={bannerSrc(b.image_path)} alt="banner" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text_main truncate">
                  {b.is_default ? 'Default banner (from Appearance)' : (b.link_url || 'No link')}
                </p>
                <p className="text-xs text-text_muted truncate">
                  <code>{b.image_path}</code>
                  {b.is_default && <span className="ml-2 bg-primary-soft text-primary px-1.5 rounded">default</span>}
                  {!active && <span className="ml-2 bg-surface_muted text-text_muted px-1.5 rounded border border-border">inactive</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={active} onCheckedChange={() => toggleActive(b)} data-testid={`banner-toggle-${b.banner_id}`} />
                <Button size="sm" variant="ghost" onClick={() => openEdit(b)}><PencilSimple size={14} weight="regular" /></Button>
                <Button size="sm" variant="ghost" className="text-danger disabled:opacity-30" disabled={b.is_default} title={b.is_default ? 'The default banner cannot be deleted' : ''} onClick={() => setConfirmDelete(b)}>
                  <Trash size={14} weight="regular" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, mode: 'create', data: null })}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog.mode === 'create' ? 'New Banner' : 'Edit Banner'}</DialogTitle></DialogHeader>
          {dialog.data && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold">Banner image (1821 × 864, ratio ~2.11:1)</Label>
                <div className="mt-2 border border-dashed border-border rounded-lg p-3 bg-surface_muted">
                  {dialog.data.image_path ? (
                    <div className="w-full aspect-[1821/864] bg-cream rounded overflow-hidden mb-2">
                      <img src={bannerSrc(dialog.data.image_path)} alt="preview" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-full aspect-[1821/864] bg-white border border-border rounded flex flex-col items-center justify-center text-text_muted text-xs mb-2">
                      <ImageIcon size={22} className="mb-1" />
                      No image uploaded
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} data-testid="banner-file-input" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="banner-upload-btn">
                    <UploadSimple size={14} className="mr-1.5" weight="bold" />
                    {uploading ? 'Uploading…' : (dialog.data.image_path ? 'Replace image' : 'Upload image')}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Click destination (optional)</Label>
                <Input placeholder="/browse?category=vehicules or https://…" value={dialog.data.link_url} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, link_url: e.target.value } })} data-testid="banner-link-input" />
                <p className="text-[11px] text-text_muted mt-1">Leave blank if the banner is decorative only.</p>
              </div>
              <div className="flex items-center justify-between border border-border rounded-md px-3 py-2">
                <div>
                  <Label className="text-xs font-semibold">Active</Label>
                  <p className="text-[11px] text-text_muted">Turn off to hide this banner from the carousel.</p>
                </div>
                <Switch checked={dialog.data.is_active !== false} onCheckedChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, is_active: v } })} data-testid="banner-active-switch" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, mode: 'create', data: null })}>Cancel</Button>
            <Button onClick={save} data-testid="banner-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete banner?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the banner from the carousel.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-danger hover:bg-danger" onClick={async () => { await remove(confirmDelete); setConfirmDelete(null); }} data-testid="banner-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
